// src/pages/inventory/commerce/shopify/tabs/CustomerSyncTab.tsx
import React, { useCallback, useState } from "react";
import { Button, Space, Typography, Alert, Statistic, Row, Col } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ImportOutlined, UserOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { importCustomersFromShopify, getCustomerSyncStatus } from "../../../../api/inventory/shopifyApi";
import type { ShopifyImportResults, ShopifySyncLog } from "../../../../types/inventory/ShopifyTypes";
import { useShopifyImportJob } from "../../../../hooks/useShopifyImportJob";
import { useNotifications } from "../../../../context/NotificationContext";
import { SectionCard, DataTable } from "../../../../components/shared";

const { Title, Text, Paragraph } = Typography;

const CustomerSyncTab: React.FC = () => {
	const [syncLogs, setSyncLogs] = useState<ShopifySyncLog[]>([]);
	const [loadingLogs, setLoadingLogs] = useState(false);
	const { showError, showSuccess } = useNotifications();

	const loadSyncLogs = useCallback(async () => {
		setLoadingLogs(true);
		try {
			const config = await import("../../../../api/inventory/shopifyApi").then((m) => m.getShopifyConfig());
			const { logs } = await getCustomerSyncStatus(config.shop_domain, 20);
			setSyncLogs(logs);
		} catch (error) {
			console.error("Failed to load sync logs:", error);
		} finally {
			setLoadingLogs(false);
		}
	}, []);

	const handleCompleted = useCallback(
		(results: ShopifyImportResults | null) => {
			const unknown = results?.indeterminate ?? 0;
			const summary = `Customer import completed. Created: ${results?.created ?? 0}, Updated: ${results?.updated ?? 0}`;

			if (unknown > 0) {
				showSuccess(
					`${summary}, Outcome unknown: ${unknown}`,
					`The Auth service result for ${unknown} customer${unknown === 1 ? "" : "s"} could not be confirmed. They may already be synchronised — do not read them as failures.`
				);
			} else {
				showSuccess(summary);
			}

			void loadSyncLogs();
		},
		[loadSyncLogs, showSuccess]
	);

	const handleFailed = useCallback(
		(_results: ShopifyImportResults | null, message: string, detail?: string) => {
			showError(message, detail);
			void loadSyncLogs();
		},
		[loadSyncLogs, showError]
	);

	const {
		phase,
		running: syncing,
		busy,
		trackingPaused,
		results: lastSyncResult,
		message: failureMessage,
		run: handleImportCustomers,
		resume: handleResumeTracking,
	} = useShopifyImportJob({
		label: "Customer",
		start: importCustomersFromShopify,
		onCompleted: handleCompleted,
		onFailed: handleFailed,
	});

	// CU-86d4372h4 — `indeterminate` is a subset of `failed`, so Failed must show only the remainder.
	const unknownOutcomes = lastSyncResult?.indeterminate ?? 0;
	const confirmedFailures = Math.max((lastSyncResult?.failed ?? 0) - unknownOutcomes, 0);
	const statSpan = unknownOutcomes > 0 ? 6 : 8;

	const logColumns: ColumnsType<ShopifySyncLog> = [
		{
			title: "Date",
			dataIndex: "created_at",
			key: "created_at",
			width: 180,
			render: (date: string) => new Date(date).toLocaleString(),
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			width: 100,
			render: (action: string) => action?.toUpperCase(),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status: string) => (
				<Space>
					{status === "success" ? (
						<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
					) : (
						<span style={{ color: "var(--mg-danger)" }}>✕</span>
					)}
					{status}
				</Space>
			),
		},
		{
			title: "Message",
			dataIndex: "message",
			key: "message",
			ellipsis: true,
		},
	];

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<Alert
					message="Import Shopify Customers"
					description="Import customers from your Shopify store. Customers are matched on their Shopify customer ID first, falling back to email address only when that ID has not been linked yet. When orders are imported, customers are automatically created if they don't exist."
					type="info"
					showIcon
					icon={<UserOutlined />}
				/>

				<SectionCard>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<div>
							<Title level={5} style={{ margin: 0 }}>
								Import All Customers
							</Title>
							<Text type="secondary">This will import all customers from Shopify. Existing customers will be updated with the latest information.</Text>
						</div>
						<Button type="primary" icon={<ImportOutlined />} onClick={handleImportCustomers} loading={syncing} disabled={busy}>
							{busy ? (phase === "queued" ? "Queued…" : "Importing…") : "Import Customers"}
						</Button>
					</div>
				</SectionCard>

				{syncing && <Alert message={phase === "queued" ? "Import queued" : "Import in progress"} description="This runs in the background. Results appear here once it finishes." type="info" showIcon />}

				{trackingPaused && (
					<Alert
						message="Status tracking paused"
						description={failureMessage ?? "Unable to check import status right now. The import may still be running."}
						type="warning"
						showIcon
						action={
							<Button size="small" onClick={handleResumeTracking}>
								Retry Status
							</Button>
						}
					/>
				)}

				{phase === "failed" && <Alert message={failureMessage ?? "Customer import failed."} description={lastSyncResult ? "The counts below are the partial progress made before the import stopped. The import did not finish." : undefined} type="error" showIcon />}

				{lastSyncResult && (
					<SectionCard title={phase === "failed" ? "Partial Import Progress" : "Last Import Results"}>
						<Row gutter={16}>
							<Col xs={24} sm={statSpan}>
								<Statistic title="Created" value={lastSyncResult.created} valueStyle={{ color: "var(--mg-success)" }} />
							</Col>
							<Col xs={24} sm={statSpan}>
								<Statistic title="Updated" value={lastSyncResult.updated} valueStyle={{ color: "var(--mg-primary)" }} />
							</Col>
							<Col xs={24} sm={statSpan}>
								<Statistic title="Failed" value={confirmedFailures} valueStyle={{ color: "var(--mg-danger)" }} />
							</Col>
							{unknownOutcomes > 0 && (
								<Col xs={24} sm={statSpan}>
									<Statistic title="Outcome Unknown" value={unknownOutcomes} valueStyle={{ color: "var(--mg-warning)" }} />
								</Col>
							)}
						</Row>

						{unknownOutcomes > 0 && (
							<Alert
								style={{ marginTop: 16 }}
								message={`${unknownOutcomes} customer outcome${unknownOutcomes === 1 ? "" : "s"} could not be confirmed`}
								description={
									<Paragraph style={{ marginBottom: 0 }}>
										These customers were sent to the Auth service, but it did not return a usable result — the connection was interrupted, or the service answered with an error before reporting what it had recorded. They may already have been created or updated, so they must not be read as confirmed unsynchronised customers. Running the import again re-checks them; customers are matched on their Shopify customer ID, so this does not create duplicates.
									</Paragraph>
								}
								type="warning"
								showIcon
							/>
						)}

						{lastSyncResult.conflicts > 0 && (
							<Alert
								style={{ marginTop: 16 }}
								message={`${lastSyncResult.conflicts} customer${lastSyncResult.conflicts === 1 ? "" : "s"} need review`}
								description={
									<>
										<Paragraph style={{ marginBottom: lastSyncResult.conflict_ids?.length ? 8 : 0 }}>
											These customers are already linked to a different Shopify customer ID. The existing link was left unchanged and was not overwritten. This is a confirmed outcome, so they are counted in the Failed total above, never as an unknown outcome.
										</Paragraph>
										{lastSyncResult.conflict_ids?.length > 0 && <Text type="secondary">Shopify customer IDs: {lastSyncResult.conflict_ids.join(", ")}</Text>}
									</>
								}
								type="warning"
								showIcon
							/>
						)}
					</SectionCard>
				)}

				{syncLogs.length > 0 && (
					<SectionCard
						title="Recent Sync History"
						extra={
							<Button size="small" onClick={loadSyncLogs} loading={loadingLogs}>
								Refresh
							</Button>
						}
					>
						<DataTable
							columns={logColumns}
							dataSource={syncLogs}
							rowKey="id"
							loading={loadingLogs}
							pagination={false}
							bare
							emptyTitle="No sync logs"
						/>
					</SectionCard>
				)}

				<SectionCard title="How Customer Sync Works">
					<Paragraph>
						<ul style={{ marginBottom: 0 }}>
							<li>Customers are matched on their Shopify customer ID first — this is the stable identifier</li>
							<li>Email address is used only as a fallback, to link an existing account that has no Shopify customer ID yet</li>
							<li>If an account is already linked to a different Shopify customer ID, that link is never overwritten — the customer is reported for review instead</li>
							<li>New customers are created with information from Shopify</li>
							<li>Existing customers are updated with the latest Shopify data</li>
							<li>Customer addresses and phone numbers are synchronized</li>
							<li>When orders are imported, customers are automatically created if they don't already exist</li>
							<li>All customer data is stored securely in the Auth microservice</li>
						</ul>
					</Paragraph>
				</SectionCard>
			</Space>
		</div>
	);
};

export default CustomerSyncTab;
